/**
 * characters module entry (owner: characters-builder).
 * Actor (4-dir animated map character), PlayerController, InputMap (keyboard → bus), Follower
 * (path-history party trail), NpcScheduleRunner (deterministic schedules), ACTOR_DEFS registry and
 * dev placeholder texture generators. See src/characters/README.md for the frame naming convention
 * and integration notes.
 */
import type Phaser from 'phaser';
import type { Facing } from '@/core/types';
import { Actor, type ActorOptions } from './Actor';

export type { ActorOptions } from './Actor';
export { Actor } from './Actor';
export { FEET_BOX, feetOverlap } from './feet';
export { SceneEvents } from './sceneEvents';
export { ActorSystem, StepOrder, type Steppable } from './ActorSystem';
export { PlayerController, type PlayerControllerOptions } from './PlayerController';
export { Follower, type FollowerOptions } from './Follower';
export { NpcScheduleRunner, type NpcScheduleRunnerOptions } from './NpcScheduleRunner';
export { installInputMap, getInputMap, INPUT_FAST_TEXT, type InputMapHandle } from './InputMap';
export { ScheduleProgram, type ScheduleActorLike, type ScheduleOptions } from './schedule';
export { PathTrail, TrailFollowLogic, type FollowOptions, type FollowOutput } from './trail';
export { bfsPath, simplifyPath, nearestWalkableTile, lineClear, pullString, type GridPoint, type TileBlocked, type BfsResult } from './pathfinding';
export { ACTOR_DEFS, getActorDef, registerDevActorAtlases, devAtlasUrls, type ActorDef, type AtlasLoaderLike } from './actorDefs';
export { ACTOR_STATES, FACINGS, ANIM_FPS, DEFAULT_STATE_FALLBACK, animKey, frameName, ensureActorAnims, forgetActorAnims, resolveStateChain, discoverStates, atlasMeta, type ActorState, type AtlasMeta } from './anims';
export { EMOTE_ATLAS, EMOTE_ICONS, EMOTE_ALIAS, emoteIconFor, resolveEmote, type EmoteIcon, type EmoteLookup, type EmoteRef } from './emotes';
export type { ShownEmote } from './Actor';
export { ensureDevActorTexture, ensureDevEmoteTexture, ensureDevTileTextures, devProcTextureKey, devEmoteTextureKey, DEV_EMOTES, DEV_EMOTE_ALIAS, DEV_FRAMES, DEV_FRAME_W, DEV_FRAME_H } from './devTextures';
export { registerShowcase, buildCharactersShowcase, buildShowcaseGrid } from './showcase';

/** Kept for integrator compatibility; identical to ActorOptions. */
export type ActorFactoryOptions = ActorOptions;

/** Creates an Actor (implements IActor). Coordinates are world pixels; facing is the initial direction. */
export function createActor(scene: Phaser.Scene, id: string, actorKey: string, x: number, y: number, facing: Facing, opts: ActorFactoryOptions = {}): Actor {
  return new Actor(scene, id, actorKey, x, y, facing, opts);
}
